import { useDeleteRequisitesStore, useGetRequisitesStore } from '@lib/store';
import { Alert, ButtonV2, Input, Modal } from '@shared/widgets';
import { Dispatch, useEffect, useState } from 'react';
import { RecipientItem } from './recipientItem';
import { GetRequisitesResponse } from '@shared/api/rest/payments';
import { RecipientTypeEnum } from '@shared/api/rest/models';

interface SelectRecipientModalType {
  isOpen: boolean;
  onClose: Dispatch<React.SetStateAction<boolean>>;
  selectedRecipient?: GetRequisitesResponse;
  setIsOpenAddNewRecipientModal: Dispatch<React.SetStateAction<boolean>>;
  setIsVisibleExternalPaymentsModal: Dispatch<React.SetStateAction<boolean>>;
  setRecipient: Dispatch<React.SetStateAction<GetRequisitesResponse | undefined>>;

  setTypeRecipientChange: Dispatch<React.SetStateAction<RecipientTypeEnum>>;
}

export const SelectRecipientModal = (props: SelectRecipientModalType) => {
  const requisitesStore = useGetRequisitesStore();
  const deleteRequisitesStore = useDeleteRequisitesStore();

  const [isOpenAlert, setIsOpenAlert] = useState<boolean>(false);
  const [selectedRecipient, setSelectedRecipient] = useState<GetRequisitesResponse | undefined>();
  const [requisiteToDelete, setRequisiteToDelete] = useState<GetRequisitesResponse>();
  const [search, setSearch] = useState<string>('');
  const [items, setItems] = useState<GetRequisitesResponse[] | null>(null);

  useEffect(() => {
    requisitesStore.get();
  }, [props.isOpen]);

  useEffect(() => {
    if (!requisitesStore.data) return setItems(null);
    if (search) {
      const searchUpper = search.toUpperCase();
      setItems(requisitesStore.data.filter((item) => item.OwnerName && item.OwnerName.toUpperCase().includes(searchUpper)));
    } else {
      setItems(null);
    }
  }, [search, requisitesStore.data]);

  const addNewRecipient = () => {
    props.setIsOpenAddNewRecipientModal(true);
    props.onClose(false);
    props.setTypeRecipientChange(RecipientTypeEnum.Add);
  };

  const editRecipient = () => {
    props.setIsOpenAddNewRecipientModal(true);
    props.onClose(false);
    props.setTypeRecipientChange(RecipientTypeEnum.Show);
  };

  const handleSelectRecipient = (requisite: GetRequisitesResponse) => {
    props.setRecipient(requisite);
    setSelectedRecipient(requisite);
  };

  const handleBackButtonClick = () => {
    props.setIsVisibleExternalPaymentsModal(true);
    props.onClose(false);
  };

  const handleDeleteAlert = (requisite: GetRequisitesResponse) => {
    setRequisiteToDelete(requisite);
    setIsOpenAlert(true);
  };

  const handleDeleteAlertConfirm = async () => {
    try {
      if (requisiteToDelete?.Id) {
        await deleteRequisitesStore.delete(requisiteToDelete?.Id);
        setIsOpenAlert(false);
      }
      if (selectedRecipient?.Id === requisiteToDelete!.Id) {
        setSelectedRecipient(undefined);
        props.setRecipient(undefined);
      }
      requisitesStore.get();
    } catch (error) {
      console.log(error);
    }
  };

  const handleDoneClick = () => {
    props.setIsVisibleExternalPaymentsModal(true);
    props.onClose(false);
  };

  const displayedItems = items || requisitesStore.data;

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} title='PLEASE SELECT OR ADD NEW RECIPIENT'>
      <div className='w-full'>
        <div className='w-full'>
          <div className='w-full text-center text-sm font-semibold text-[#666] uppercase'>Select Recipient</div>
          <Input variant='outlined' rounded name='Search Recipient' placeholder='SEARCH' value={search} onChange={setSearch} />
          {displayedItems.length || requisitesStore.data.length ? (
            <div className='custom-scroll mt-4 -mr-8 mb-9 ml-0 h-[280px] w-[356px] overflow-y-auto'>
              <div className='mr-4'>
                {!displayedItems.length && requisitesStore.data.length && search ? (
                  <div className='w-full text-center text-sm font-semibold text-[#666] uppercase'>{`No results for: "${search}"`}</div>
                ) : null}
                {displayedItems.map((requisite, index) => (
                  <div key={index} className='border-t border-t-[#97815e33]'>
                    <RecipientItem
                      item={requisite}
                      query={search}
                      isActive={requisite.Id === props.selectedRecipient?.Id}
                      onSelect={handleSelectRecipient}
                      onDelete={handleDeleteAlert}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className='my-7 text-center text-xs font-semibold text-[#666] uppercase'>
              Your recipient list is empty, you can add them by clicking the button "Add new recipient"
            </div>
          )}
        </div>
        <ButtonV2 fullWidth color='primary' className='mt-2' onClick={addNewRecipient}>
          Add new recipient
        </ButtonV2>
        {selectedRecipient && (
          <ButtonV2 fullWidth color='primary' className='mt-2' onClick={editRecipient}>
            Show Details
          </ButtonV2>
        )}
        <div className='mt-4 flex w-full flex-row items-center justify-between gap-4'>
          <ButtonV2 fullWidth color='outlined' className='button new-recipient-btn' onClick={handleBackButtonClick}>
            Back
          </ButtonV2>
          <ButtonV2 fullWidth color='primary' disabled={selectedRecipient ? false : true} onClick={handleDoneClick}>
            Done
          </ButtonV2>
        </div>
        <Alert title='Removing The Recipient' isOpen={isOpenAlert} onClose={setIsOpenAlert} onDelete={handleDeleteAlertConfirm}>
          {`Are you sure you want to remove a recipient «${requisiteToDelete?.OwnerName}»?`}
        </Alert>
      </div>
    </Modal>
  );
};
